import { useEffect, useRef, useState } from 'react';
import {
  CheckCircle2,
  Clock3,
  FileText,
  FolderOpen,
  LoaderCircle,
  RotateCw,
  TriangleAlert,
} from 'lucide-react';
import { OperatorErrorText } from '../renderer/TechnicalDetails';
import type { PaigeContentCentralType, PaigeDestinationsBridge } from './paige.global';
import type { PaigeCcCheck, PaigeCcExample } from './product-destination-types';
import { reasonOf } from './use-content-central';
import './content-central-sheet.css';

export interface ContentCentralCheckProps {
  bridge: PaigeDestinationsBridge;
  destinationId: string;
  /** The matching on screen: the catalog and the type map, not saved yet. */
  catalog: string;
  types: PaigeContentCentralType[];
}

type Verdict =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'failed'; error: string }
  | { kind: 'done'; check: PaigeCcCheck; asked: string };

/**
 * "Check with Content Central" (CapturePoint's matching window, right side): Content Central reviews the whole
 * matching on screen and gives its verdict, its first problem in its own words, and the example folders and file
 * name it builds for each type from sample values. Nothing changes in Content Central; Save keeps the matching. A
 * change after a check says it has not been checked yet.
 */
export function ContentCentralCheck(props: ContentCentralCheckProps) {
  const { catalog } = props;
  const { verdict, changed, check } = useCheck(props);
  return (
    <section
      className="paige-cc-verdict"
      aria-live="polite"
      aria-label="Check with Content Central"
    >
      <VerdictLine verdict={verdict} changed={changed} />
      <button
        type="button"
        className="secondary-button small"
        disabled={!catalog || verdict.kind === 'busy'}
        onClick={check}
      >
        <RotateCw size={14} aria-hidden="true" /> Check with Content Central
      </button>
      {verdict.kind === 'done' && !changed && <Examples examples={verdict.check.examples} />}
    </section>
  );
}

/** The check and its verdict; a change of the matching after it reads as not checked yet. */
function useCheck({ bridge, destinationId, catalog, types }: ContentCentralCheckProps) {
  const [verdict, setVerdict] = useState<Verdict>({ kind: 'idle' });
  const matching = JSON.stringify({ catalog, types });
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  const check = () => {
    setVerdict({ kind: 'busy' });
    const failed = (failure: unknown) =>
      setVerdict({
        kind: 'failed',
        error: reasonOf(failure, 'Content Central could not check this matching'),
      });
    bridge.ccCheck?.(destinationId, { catalog, types }).then(
      (answer) => mounted.current && setVerdict({ kind: 'done', check: answer, asked: matching }),
      (failure: unknown) => mounted.current && failed(failure),
    );
  };
  return { verdict, changed: verdict.kind === 'done' && verdict.asked !== matching, check };
}

function VerdictLine({ verdict, changed }: { verdict: Verdict; changed: boolean }) {
  if (verdict.kind === 'busy')
    return (
      <p className="paige-cc-note is-busy" role="status">
        <LoaderCircle className="spin" size={16} aria-hidden="true" />
        <span>
          <strong>Checking with Content Central…</strong> This can take a little while.
        </span>
      </p>
    );
  if (verdict.kind === 'failed')
    return (
      <div className="paige-cc-note is-bad" role="alert">
        <TriangleAlert size={16} aria-hidden="true" />
        <span>
          <strong>Content Central couldn&apos;t check this matching</strong>
          <OperatorErrorText message={verdict.error} />
        </span>
      </div>
    );
  if (verdict.kind === 'idle' || changed)
    return (
      <p className="paige-cc-note">
        <Clock3 size={16} aria-hidden="true" />
        <span>
          <strong>{changed ? 'Changes not checked yet' : 'Not checked yet'}</strong> Content Central
          checks the whole matching, and builds an example for each type.
        </span>
      </p>
    );
  return <Answer check={verdict.check} />;
}

function Answer({ check }: { check: PaigeCcCheck }) {
  const [first] = check.blockers;
  if (first)
    return (
      <div className="paige-cc-note is-bad" role="alert">
        <TriangleAlert size={16} aria-hidden="true" />
        <span>
          <strong>Content Central found something to fix</strong>
          <OperatorErrorText message={first} />
          {check.blockers.length > 1 && (
            <small>And {check.blockers.length - 1} more after it.</small>
          )}
        </span>
      </div>
    );
  return (
    <p className="paige-cc-note is-ok" role="status">
      <CheckCircle2 size={16} aria-hidden="true" />
      <span>
        <strong>Content Central accepts this matching</strong> Nothing changes in Content Central.
        Save to use it.
        {check.warnings.map((warning) => (
          <small key={warning}>{warning}</small>
        ))}
      </span>
    </p>
  );
}

function Examples({ examples }: { examples: PaigeCcExample[] }) {
  if (examples.length === 0) return null;
  return (
    <div className="paige-cc-examples">
      <small>Examples built by Content Central · sample values</small>
      {examples.map((example) => (
        <div key={`${example.paigeType}:${example.documentType}`} className="paige-cc-path">
          <span className="paige-cc-path-type">{example.paigeType}</span>
          <span className="paige-cc-path-pieces">
            {example.folders.map((folder, index) => (
              <span key={index} className="paige-cc-path-folder">
                <FolderOpen size={14} aria-hidden="true" />
                {folder}
                <i aria-hidden="true">›</i>
              </span>
            ))}
            <span className="paige-cc-path-file">
              <FileText size={14} aria-hidden="true" />
              <b>{example.fileName}</b>
            </span>
          </span>
          {example.diagnostics.map((note) => (
            <small key={note}>{note}</small>
          ))}
        </div>
      ))}
    </div>
  );
}
